import { useState } from 'react'
import { ACCOUNT_ROLES, type AccountRole, type AccountTeam, type NewAccount, type TeamResult } from '../../pages/admin/accountStore'

const MIN_PASSWORD_LENGTH = 12

const FIELD = 'min-h-11 w-full rounded-control border border-line-strong px-2.5 text-small'
const LABEL = 'mb-1 block text-caption font-bold text-ink-muted'

/**
 * docs/product/personas.md: an administrator creates an account and assigns
 * one role. The password is the one the administrator sets for the person. An
 * agent account also carries the employee record and first team placement it
 * needs to use the workspace, and a supervisor account leads the teams chosen
 * here: only a team nobody leads yet, so creating an account never takes a
 * team from its supervisor. A team that does not exist yet can be created
 * without leaving the form.
 */
export default function AccountForm({
  teams,
  saving,
  error,
  onSave,
  onCancel,
  onCreateTeam,
}: {
  teams: AccountTeam[]
  saving: boolean
  error: string | null
  onSave: (account: NewAccount) => void
  onCancel: () => void
  onCreateTeam: (name: string) => Promise<TeamResult>
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState<AccountRole>('agent')
  const [employeeIdentifier, setEmployeeIdentifier] = useState('')
  const [seniorityDate, setSeniorityDate] = useState('')
  const [teamId, setTeamId] = useState(teams[0]?.id ?? '')
  const [teamQuery, setTeamQuery] = useState('')
  const [title, setTitle] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [leadTeamIds, setLeadTeamIds] = useState<Set<string>>(new Set())
  const [newTeamOpen, setNewTeamOpen] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [creatingTeam, setCreatingTeam] = useState(false)
  const [teamError, setTeamError] = useState<string | null>(null)

  // The agent's team is picked from the teams matching the search; a choice
  // the search has since ruled out falls back to the first match.
  const teamMatches = teams.filter((team) => team.name.toLowerCase().includes(teamQuery.trim().toLowerCase()))
  const selectedTeamId = teamMatches.some((team) => team.id === teamId) ? teamId : (teamMatches[0]?.id ?? '')

  // The agent's first placement has to fall while the team exists.
  const selectedTeam = teams.find((team) => team.id === selectedTeamId)
  const placementProblem =
    effectiveFrom === '' || !selectedTeam
      ? null
      : selectedTeam.openDate && effectiveFrom < selectedTeam.openDate
        ? `${selectedTeam.name} opens on ${selectedTeam.openDate}.`
        : selectedTeam.closeDate && effectiveFrom > selectedTeam.closeDate
          ? `${selectedTeam.name} closed on ${selectedTeam.closeDate}.`
          : null

  const isAgent = role === 'agent'
  const isSupervisor = role === 'supervisor'
  const unledTeams = teams.filter((team) => team.supervisorId === null)
  const showsTeamCreation = isAgent || isSupervisor
  const canSave =
    email.trim() !== '' &&
    password.length >= MIN_PASSWORD_LENGTH &&
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    (!isAgent ||
      (employeeIdentifier.trim() !== '' && seniorityDate !== '' && selectedTeamId !== '' && title.trim() !== '' && effectiveFrom !== '' && placementProblem === null))

  async function createTeam() {
    if (newTeamName.trim() === '' || creatingTeam) return
    setCreatingTeam(true)
    setTeamError(null)
    const result = await onCreateTeam(newTeamName.trim())
    setCreatingTeam(false)
    if (!result.ok) {
      setTeamError(result.message)
      return
    }
    // The team just made is the one this account is for.
    if (isAgent) {
      setTeamId(result.team.id)
      setTeamQuery('')
    }
    if (isSupervisor) setLeadTeamIds((current) => new Set(current).add(result.team.id))
    setNewTeamName('')
    setNewTeamOpen(false)
  }

  function toggleLeadTeam(id: string) {
    setLeadTeamIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function save() {
    if (!canSave || saving) return
    onSave({
      email: email.trim(),
      password,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      role,
      leadTeamIds: isSupervisor ? Array.from(leadTeamIds) : undefined,
      employee: isAgent
        ? { employeeIdentifier: employeeIdentifier.trim(), seniorityDate, teamId: selectedTeamId, title: title.trim(), effectiveFrom }
        : undefined,
    })
  }

  return (
    <form
      className="flex flex-col gap-3 rounded-surface border border-line bg-canvas p-3"
      aria-label="Create account"
      onSubmit={(event) => {
        event.preventDefault()
        save()
      }}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label htmlFor="account-first-name" className={LABEL}>
            First name
          </label>
          <input id="account-first-name" type="text" maxLength={100} value={firstName} onChange={(event) => setFirstName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="account-last-name" className={LABEL}>
            Last name
          </label>
          <input id="account-last-name" type="text" maxLength={100} value={lastName} onChange={(event) => setLastName(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="account-email" className={LABEL}>
            Email
          </label>
          <input id="account-email" type="email" autoComplete="off" value={email} onChange={(event) => setEmail(event.target.value)} className={FIELD} />
        </div>
        <div>
          <label htmlFor="account-role" className={LABEL}>
            Role
          </label>
          <select id="account-role" value={role} onChange={(event) => setRole(event.target.value as AccountRole)} className={FIELD}>
            {ACCOUNT_ROLES.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="account-password" className={LABEL}>
            Password
          </label>
          <input
            id="account-password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            aria-describedby="account-password-hint"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className={FIELD}
          />
          <p id="account-password-hint" className="mt-1 text-caption text-ink-subtle">
            At least {MIN_PASSWORD_LENGTH} characters. Give it to the person directly.
          </p>
          <label className="mt-1 flex min-h-11 items-center gap-2 text-small">
            <input type="checkbox" checked={showPassword} onChange={(event) => setShowPassword(event.target.checked)} className="size-5" />
            Show password
          </label>
        </div>
      </div>

      {isAgent && (
        <fieldset className="grid grid-cols-1 gap-3 border-t border-line pt-3 sm:grid-cols-2">
          <legend className="mb-2 text-caption font-bold tracking-wide text-ink-subtle uppercase">Employee record</legend>
          <div>
            <label htmlFor="account-employee-identifier" className={LABEL}>
              Employee identifier
            </label>
            <input id="account-employee-identifier" type="text" maxLength={20} value={employeeIdentifier} onChange={(event) => setEmployeeIdentifier(event.target.value)} className={FIELD} />
          </div>
          <div>
            <label htmlFor="account-seniority-date" className={LABEL}>
              Seniority date
            </label>
            <input id="account-seniority-date" type="date" value={seniorityDate} onChange={(event) => setSeniorityDate(event.target.value)} className={FIELD} />
          </div>
          <div>
            <label htmlFor="account-team-search" className={LABEL}>
              Search teams
            </label>
            <input
              id="account-team-search"
              type="search"
              autoComplete="off"
              value={teamQuery}
              onChange={(event) => setTeamQuery(event.target.value)}
              aria-controls="account-team"
              className={FIELD}
            />
          </div>
          <div>
            <label htmlFor="account-team" className={LABEL}>
              Team
            </label>
            <select id="account-team" value={selectedTeamId} onChange={(event) => setTeamId(event.target.value)} className={FIELD}>
              {teamMatches.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
            <p role="status" className="mt-1 text-caption text-ink-subtle">
              {teamMatches.length === 0
                ? 'No team matches your search.'
                : teamQuery.trim() !== ''
                  ? `${teamMatches.length} ${teamMatches.length === 1 ? 'team matches' : 'teams match'} your search.`
                  : ''}
            </p>
          </div>
          <div>
            <label htmlFor="account-title" className={LABEL}>
              Title
            </label>
            <input id="account-title" type="text" maxLength={100} value={title} onChange={(event) => setTitle(event.target.value)} className={FIELD} />
          </div>
          <div>
            <label htmlFor="account-effective-from" className={LABEL}>
              On the team from
            </label>
            <input
              id="account-effective-from"
              type="date"
              min={selectedTeam?.openDate}
              max={selectedTeam?.closeDate ?? undefined}
              aria-describedby={placementProblem ? 'account-placement-problem' : undefined}
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
              className={FIELD}
            />
            {placementProblem && (
              <p id="account-placement-problem" role="alert" className="mt-1 text-caption font-semibold text-error">
                {placementProblem} Choose a date within the dates the team exists.
              </p>
            )}
          </div>
        </fieldset>
      )}

      {isSupervisor && (
        <fieldset className="flex flex-col gap-1 border-t border-line pt-3">
          <legend className="mb-2 text-caption font-bold tracking-wide text-ink-subtle uppercase">Teams to lead</legend>
          {unledTeams.length === 0 ? (
            <p className="text-small text-ink-muted">Every open team already has a supervisor.</p>
          ) : (
            unledTeams.map((team) => (
              <label key={team.id} className="flex min-h-11 items-center gap-2 text-small">
                <input type="checkbox" checked={leadTeamIds.has(team.id)} onChange={() => toggleLeadTeam(team.id)} className="size-5" />
                {team.name}
              </label>
            ))
          )}
        </fieldset>
      )}

      {showsTeamCreation && (
        <div className="flex flex-col gap-2">
          {newTeamOpen ? (
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[200px] flex-1">
                <label htmlFor="new-team-name" className={LABEL}>
                  New team name
                </label>
                <input
                  id="new-team-name"
                  type="text"
                  maxLength={100}
                  value={newTeamName}
                  onChange={(event) => setNewTeamName(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      void createTeam()
                    }
                  }}
                  className={FIELD}
                />
              </div>
              <button
                type="button"
                disabled={newTeamName.trim() === '' || creatingTeam}
                onClick={() => void createTeam()}
                className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2 disabled:opacity-40"
              >
                {creatingTeam ? 'Creating…' : 'Add team'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNewTeamOpen(false)
                  setNewTeamName('')
                  setTeamError(null)
                }}
                className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => setNewTeamOpen(true)}
                className="flex min-h-11 items-center justify-center rounded-control border border-line-strong px-4 text-small font-bold hover:bg-surface-2"
              >
                Create new team
              </button>
            </div>
          )}
          {teamError && (
            <p role="alert" className="text-caption font-semibold text-error">
              {teamError}
            </p>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-caption font-semibold text-error">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSave || saving}
          className="flex min-h-11 items-center justify-center rounded-control bg-primary px-4 text-small font-bold text-primary-ink hover:bg-primary-hover disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create account'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex min-h-11 items-center justify-center rounded-control px-4 text-small font-bold text-ink-muted hover:bg-surface-2"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
